<!-- Capture status once to avoid multiple async subscriptions -->
<ng-container *ngIf="phxFile.status$ | async as status">
  <!-- Progress overlay when details shown (non-tile layouts) -->
  <div *ngIf="status === PHX_FILE_LOADING_STATUS.PROGRESS && !phxTileMode" class="phx-file-progress-background"
    [style.width.%]="phxFile.progress$ | async"></div>

  <!-- File Preview Thumbnail -->
  <div class="phx-file-preview" [class.phx-file-obfuscate]="phxObfuscate"
    [style.width]="!phxShowDetails || !phxIsEditMode ? '100%' : null"
    [style.height]="!phxShowDetails || !phxIsEditMode ? '100%' : null" (contextmenu)="onContextMenu($event)">
    <!-- Image Preview (tile mode keeps showing a valid image on validation errors so the user can
         compare it with the listed issues; genuinely unloadable images fall back to the icon) -->
    <img *ngIf="isImage && !imageLoadFailed && (!phxTileMode ? status !== PHX_FILE_LOADING_STATUS.ERROR : (status !== PHX_FILE_LOADING_STATUS.START && status !== PHX_FILE_LOADING_STATUS.PROGRESS))"
      [src]="safeUrl" crossOrigin="anonymous" class="phx-file-image" (error)="onFileLoadError()" />

    <!-- Video Preview -->
    <video *ngIf="isVideo && !imageLoadFailed && (!phxTileMode ? status !== PHX_FILE_LOADING_STATUS.ERROR : (status !== PHX_FILE_LOADING_STATUS.START && status !== PHX_FILE_LOADING_STATUS.PROGRESS))"
      [src]="safeUrl" crossOrigin="anonymous" class="phx-file-video" (error)="onFileLoadError()">
    </video>

    <!-- PDF first-page preview (tile mode only; falls back to the icon below if it can't render) -->
    <pdf-viewer
      *ngIf="isPDF && phxTileMode && !pdfLoadFailed && status !== PHX_FILE_LOADING_STATUS.START && status !== PHX_FILE_LOADING_STATUS.PROGRESS"
      class="phx-file-tile-pdf" [src]="phxFile.url" [page]="1" [show-all]="false" [render-text]="false"
      [original-size]="false" [fit-to-page]="true" [zoom-scale]="'page-width'" (error)="onPdfLoadError()"></pdf-viewer>

    <!-- PDF Icon (non-tile layouts, or tile-mode fallback when the preview fails to render) -->
    <div *ngIf="isPDF && (!phxTileMode || pdfLoadFailed)" class="phx-file-icon">
      <i phx-icon [phxIcon]="documentIcon" color="currentColor"></i>
    </div>

    <!-- GeoJSON / JSON Icon -->
    <div *ngIf="isGeoJson && (!phxTileMode || status === PHX_FILE_LOADING_STATUS.DONE)" class="phx-file-icon">
      <i phx-icon [phxIcon]="documentIcon" color="currentColor"></i>
    </div>

    <!-- Unknown File Type Icon or Error Fallback for Image/Video (tile mode only falls back when the
         media genuinely failed to load, not on consumer-reported validation errors) -->
    <div *ngIf="(isUnknownFileType || ((isImage || isVideo) && (!phxTileMode ? status === PHX_FILE_LOADING_STATUS.ERROR : imageLoadFailed))) && (!phxTileMode || (status !== PHX_FILE_LOADING_STATUS.START && status !== PHX_FILE_LOADING_STATUS.PROGRESS))"
      class="phx-file-icon">
      <i phx-icon [phxIcon]="imageIcon" color="currentColor"></i>
    </div>
  </div>

  <!-- Tile layout: centered spinner while the file is uploading/processing -->
  <div
    *ngIf="phxTileMode && (status === PHX_FILE_LOADING_STATUS.START || status === PHX_FILE_LOADING_STATUS.PROGRESS)"
    class="phx-file-tile-loading">
    <phx-spinner phxSize="large"></phx-spinner>
  </div>

  <!-- Tile layout: file-size badge over the image (shown once not loading, including error tiles) -->
  <div
    *ngIf="phxTileMode && phxFile.size && status !== PHX_FILE_LOADING_STATUS.START && status !== PHX_FILE_LOADING_STATUS.PROGRESS"
    class="phx-file-tile-size-badge">
    {{ formatFileSize(phxFile.size) }}
  </div>

  <!-- File Details (shown when phxShowDetails is true AND in edit mode) -->
  <div *ngIf="phxShowDetails && phxIsEditMode" class="phx-file-details">
    <div class="phx-file-name-container">
      <span class="phx-file-name" [phxMiddleTruncate]="phxFile.name"></span>
    </div>
    <span
      *ngIf="(status === PHX_FILE_LOADING_STATUS.START || status === PHX_FILE_LOADING_STATUS.PROGRESS) && phxFile.size"
      class="phx-file-size">
      {{ uploadedSize }} {{ 'out of' | phxTranslate }} {{ formatFileSize(phxFile.size) }} {{ 'uploaded' | phxTranslate }}
    </span>
    <span *ngIf="status === PHX_FILE_LOADING_STATUS.DONE" class="phx-file-size">
      {{ formatFileSize(phxFile.size) }}
    </span>
    <div *ngIf="status === PHX_FILE_LOADING_STATUS.ERROR" class="phx-file-error-container">
      <span *ngIf="phxFile.error" class="phx-file-error-message">{{ phxFile.errorMessage }}</span>
      <ng-container *ngIf="showRetry">
        <i phx-icon [size]="16" [phxIcon]="bulletIcon"></i>
        <a class="phx-file-action-button phx-file-action-button-retry" (click)="onRetry($event)"
          (keydown.enter)="onRetry($event)" (keydown.space)="onRetry($event)">{{ 'Retry' | phxTranslate }}</a>
      </ng-container>
    </div>
  </div>

  <!-- Actions (Remove button when in edit mode with details) -->
  <div *ngIf="phxIsEditMode && phxShowDetails" class="phx-file-suffix">
    <button type="button" phx-icon-button [phxIcon]="crossIcon" phxIconButtonSize="small" [disabled]="phxFile.disabled" 
      class="phx-file-action-button phx-file-action-button-remove" (click)="onRemove($event)">
    </button>
  </div>

  <!-- Actions for thumbnail view (when in edit mode without details) -->
  <div *ngIf="phxIsEditMode && !phxShowDetails" class="phx-file-actions">
    <button type="button" phx-icon-button [phxIcon]="crossIcon" [phxIconButtonSize]="'extra-small'" [disabled]="phxFile.disabled" 
      class="phx-file-action-button phx-file-action-button-remove" (click)="onRemove($event)">
    </button>
  </div>
</ng-container>
